<template>
    <input ref="input" type="text" class="form-control"
        :class="{ 'form-error': isError }"
        :name="name"
        :errors="errors"
        :placeholder="placeholder"
        :readonly="readonly"
        v-model="timeInput"
        @change="verifyTime"
        @update="verifyTime"
        @blur="verifyTime"
    />
</template>
<script>
import {defineComponent} from 'vue';
export default defineComponent ({
    emits: ['update', 'update:time', 'error'],
    props: {
        name: {
            type: String,
            required: false,
            default: ''
        },
        placeholder: {
            type: String,
            required: false,
            default: ''
        },
        time: {
            required: false,
            default: ''
        },
        dateFull: {
            required: true,
            default: ''
        },
        tzoffset: {
            type: Number,
            required: false,
        },
        readonly: {
            type: Boolean,
            default: false,
            required: false,
        },
        errors: {
            type: Boolean,
            required: true,
        }
    },

    data () {
        return {
            isError: false,
            hour: '',
            timeInput: this.time,
        }
    },

    watch: {
        //  Need this to ensure changes made by parent update here
        time(value) {
            this.timeInput = value;
        },

        // needed to do live-formatting of times
        timeInput(newValue, oldValue) {
            this.timeCleave(newValue, oldValue);
        },

        // Look for errors from parent
        errors: {
            handler: function (newValue) {
                // console.log ('Error: ', this.name, newValue);
                if (newValue === true) {
                    this.isError = true;
                } else {
                    this.isError = false;
                }

                return true;
            }
        }
    },

    methods: {
        // Our own cleave for time
        timeCleave(newValue, oldValue) {
            let lastChar = newValue.slice(-1);

            switch (newValue.length) {
                case 0:
                    this.hour = undefined;
                    break;;
                case 1:
                    this.hour = undefined;
                    // Don't allow anything but numbers for the first character
                    if (! lastChar.match(/[0-9]/)) {
                        this.timeInput = oldValue;
                    } 
                    if (lastChar > 3) {
                        this.timeInput = '0' + lastChar + ':';
                        this.hour = '0' + lastChar;
                    }
                    break;;
                case 2:
                    // if user types in a colon, assume the hour field is complete and zero pad if necessary
                    if (this.hour) {
                        break;
                    } else if (lastChar.match(/a/i)) {
                        this.hour = '0' + oldValue;
                        this.timeInput = this.hour + ':00';
                    } else if (lastChar.match(/p/i)) {
                        this.hour = 12 + oldValue*1;
                        this.timeInput = this.hour + ':00';
                    } else if (lastChar === ':') {
                        this.hour = '0' + oldValue;
                        this.timeInput = this.hour + ':';
                    // allow for a decimal based time
                    } else if (lastChar === '.') {
                        this.hour = '0' + oldValue;
                        this.timeInput = this.hour + '.';
                    // auto "colon-ate" if 2 digits have been selected
                    } else if (oldValue > 3 && lastChar !== ':') {
                        this.timeInput = oldValue;
                    // auto "colon-ate" if 2 digits have been selected
                    } else if (lastChar.match(/[0-9]/)) {
                        this.timeInput = newValue + ':';
                        this.hour = newValue;
                    } else {
                        this.timeInput = oldValue;
                    }
                    break;;
                case 3:
                    // always a colon or decimal
                    break;;
                case 4:
                    let delimiter = newValue.substr(2,1);
                    if (lastChar === '.') {
                        this.timeInput = this.hour + '.';
                    } else if (lastChar.match(/a/i)) {
                        if (oldValue === '12:') {
                            this.hour = 0;
                            this.timeInput = '00:00';
                        } else {
                            this.timeInput = this.hour + ':00';
                        }
                    } else if (lastChar.match(/p/i)) {
                        if (oldValue === '12:') {
                            this.timeInput = '12:00';
                        } else {
                            this.timeInput = 12 + this.hour*1 + ':00';
                        }
                    } else if (delimiter === ':' && ! lastChar.match(/[0-5]/)) {
                        this.timeInput = oldValue;
                    } else if (! lastChar.match(/[0-9]/)) {
                        this.timeInput = oldValue;
                    }
                    break;;
                case 5:
                    let delimiter2 = newValue.substr(2,1);
                    let minutes2 = newValue.substr(3,1);
                    if (delimiter2 === '.' && lastChar.match(/a/i)) {
                        if (this.hour === 12) {
                            this.hour = 0;
                            this.timeInput = '00.' + this.minutes2;
                        } else {
                            this.timeInput = oldValue;
                        }
                    } else if (delimiter2 === '.' && lastChar.match(/p/i)) {
                        if (this.hour === 12) {
                            this.timeInput = oldValue;
                        } else {
                            this.timeInput = 12 + this.hour*1 + '.' + minutes2;
                        }
                    } else if (! lastChar.match(/[0-9]/)) {
                            this.timeInput = newValue.substr(0,4);
                    }
                    break;;
                case 6:
                    if (this.hour === undefined) this.hour = oldValue.substr(0,2);
                    let delimiter3 = newValue.substr(2,1);
                    let minutes3 = newValue.substr(3,2);

                    if (lastChar.match(/a/i)) {
                        if (delimiter3 === '.' && this.hour === '12') {
                            this.timeInput = '00.' + minutes3;
                        } else if (this.hour === '12') {
                            this.timeInput = '00:' + minutes3;
                        } else {
                            this.timeInput = oldValue;
                        }
                    } else if (lastChar.match(/p/i)) {
                        if (this.hour === '12') {
                            this.timeInput = oldValue
                        } else if (delimiter3 === '.')  {
                            this.timeInput = this.hour*1+12 + '.' + minutes3;
                        } else {
                            this.timeInput = this.hour*1+12 + ':' + minutes3;
                        }
                    } else {
                        this.timeInput = oldValue;
                    }
                    break;;

                default:
                   this.timeInput = oldValue;
            }
        },

        // Help make sure the data entered looks like a time
        // target is a custom time string in this format: HH[:.]MM[aApP] where HH can be 0 - 48, and the delimter can
        // be a traditional minute (:) or a portion of an hour (.) 
        verifyTime( {target}, date = this.dateFull, tzoff = this.tzoffset, initialLoad = false ) {
            let errorMessage = '';
            // Don't remove errors if set by parent
            if (this.errors === false || this.errors === undefined) this.isError = false;

            // console.log (target.value.length);
            if (target.value.length === 0) {
                this.$emit('update', { name: target.name, value: '' }); //Updates to the week object
                this.$emit('update:time', ''); // Updates the timecard-day :time.sync prop
                return true;
            }
            
            // Parse the time entered and figure out how to convert to a time.
            let delim = target.value.substr(2,1);
            let hour = target.value.substr(0,2);
            let min = target.value.substr(3,2);
            let mod = target.value.substr(5,1);

            if (hour.length < 2){
                hour = "0" + hour;
            }

            // assume 00 if user did not type in
            if (! min) min = "00";
            if (! delim) delim = ':';

            if (delim === ':' && min.length !== 2) {
                this.isError = true;
                errorMessage += ' Bad time entered: ' + target.value;
            }

            if (hour > 48) {
                this.isError = true;
                errorMessage += ' Hour is more than 48.  Did this employee work more than 2 days straight?';
                // console.log ('wrong hour');
            } else if (hour > 12) {
                mod = "";
            } else if (mod === '' && (hour > 0 && hour < 13)) {
                // console.log ('am/pm ?');
            }
            // console.log (hour, min, mod);

            // Convert from a decimal to minute
            if (delim === '.') {
                min = this.convertDecToMin('.'+min);
                delim = ':';
            }

            // Normalized 24-hour time, e.g. 23:00 to display (not saved/stored)
            // We will be using the hour and min to compute and update the time
            let timeEntered = String (hour + delim + min + mod);

            // Take into account the time entered and local (current user) timezone offset
            const enteredOffset = isNaN(tzoff) ? 0 : parseInt(tzoff);
            const localOffset = new Date(date).getTimezoneOffset();
            let hourOffset = 0;
            let minuteOffset = 0;

            // Is there a diff between local time and the time_entered? Need to account for it.
            if (localOffset !== enteredOffset) {
                hourOffset -= Math.floor((localOffset - enteredOffset) / 60);
                minuteOffset -= (localOffset - enteredOffset) % 60; 
                // console.log('Offset difference', localOffset, enteredOffset, hourOffset, minuteOffset);
            }

            // Take timecard date and add our computed hours/minutes to it
            let timecardDate = new Date(date);
            let adjustedDateTime = new Date(timecardDate.getFullYear(), timecardDate.getMonth(), timecardDate.getDate(), parseInt(hour) + hourOffset, parseInt(min) + minuteOffset);

            // Update screen/time object
            this.timeInput = timeEntered;
            this.$emit('update:time', timeEntered); // Updates the timecard-day :time.sync prop with a short time as user entered it
            this.$emit('update', { name: target.name, value: adjustedDateTime.toISOString() }, initialLoad ? true : false); //Updates to the week object with a tz adjusted time string
            // console.log('Target', target);
            // console.log('Date Full', this.dateFull);
            // console.log('Timezone Entered/Local', this.tzoffset, localOffset);
            // console.log('Time entered', timeEntered);
            // console.log(`adjustedDateTime/UTC ${adjustedDateTime.toISOString()} ${adjustedDateTime.getHours()}:${adjustedDateTime.getMinutes()} ${adjustedDateTime.toISOString()}`);

            if (this.isError) {
                this.$emit('error', errorMessage);
            } else {
                this.$emit('error', '');
            }
        },

        // Convert decimal hours into minutes
        convertDecToMin(multiplier) {
            let minutes = Math.round(60 * multiplier);

            if (minutes < 10) {
                return '0' + minutes;
            } else {
                return minutes;
            }
        },

        // pad 1-digit months/days
        pad(n) {
            return n<10 ? '0'+n : n;
        },
    },
});
</script>
